import { Menu, User, Settings, LogOut, Shield, X, Link as LinkIcon, QrCode } from 'lucide-react';
import { toast } from '@/lib/toast';
import { copyToClipboard } from '@/lib/copyToClipboard';
import { SimpleQrView } from '@/components/menu/SimpleQrView';

import { Button } from './ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useIsMobile } from '@/hooks/use-mobile';
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { MVP_FEATURES } from '@/lib/mvpFeatures';
import {
	Drawer,
	DrawerContent,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from '@/components/ui/drawer';
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
	SheetClose,
} from '@/components/ui/sheet';
import { EditableAvatar } from '@/components/profile/EditableAvatar';
import { Separator } from './ui/separator';
import { toolbarIconButton, toolbarPopoverButton } from '@/lib/buttonStyles';

interface MobileMenuProps {
	triggerVariant?: 'floating' | 'toolbar';
	triggerClassName?: string;
	triggerIconClassName?: string;
	/** Optional custom trigger element. When provided, it replaces the default hamburger button. */
	trigger?: React.ReactNode;
}

export const MobileMenu = ({
	triggerVariant = 'floating',
	triggerClassName,
	triggerIconClassName,
	trigger: triggerProp,
}: MobileMenuProps) => {
	const { user, signOut } = useAuth();
	const { profile, refetch } = useProfile();
	const { isAdmin } = useIsAdmin();
	const isMobile = useIsMobile();
	const navigate = useNavigate();
	const location = useLocation();
	const [open, setOpen] = useState(false);

	const [qrOpen, setQrOpen] = useState(false);

	// Derive username from /u/:username or /preview using current profile
	const usernameMatch = location.pathname.match(/^\/u\/([^/]+)/);
	const qrUsername = usernameMatch?.[1] ?? profile?.username ?? null;
	const qrUrl = qrUsername ? `https://cardsimp.com/u/${qrUsername}` : window.location.href;

	const handleOpenQr = () => {
		setOpen(false);
		setTimeout(() => setQrOpen(true), 100);
	};

	const isInventoryActive = location.pathname === '/inventory';
	const isStorefrontActive = location.pathname.startsWith('/u/') || location.pathname === '/preview';
	const isTransactionsActive = location.pathname === '/transactions';
	const isAdminActive = location.pathname === '/admin';
	
	const isMarketActive = location.pathname === '/market' || location.pathname.startsWith('/market/');

	const handleNavigation = (path: string) => {
		navigate(path);
		setOpen(false);
	};

	const handleSignOut = async () => {
		await signOut();
		setOpen(false);
	};

	const handleCopyLink = async () => {
		const ok = await copyToClipboard(window.location.href);
		if (ok) {
			toast.forceSuccess('Link copied to clipboard!');
		} else {
			toast.error('Failed to copy link');
		}
		setOpen(false);
	};


	const navItemClass = cn(
		'w-full justify-start text-base font-medium h-11',
		'border border-transparent hover:border-white/20',
		'hover:bg-white/10 hover:text-white/80',
		'transition-all duration-200'
	);

	const activeClass = 'text-[hsl(50,100%,68%)] hover:text-[hsl(50,100%,68%)]';

	const isToolbarTrigger = triggerVariant === 'toolbar';

	const defaultTrigger = (
		<Button
			variant="ghost"
			size="icon"
			className={cn(
				isToolbarTrigger
					? cn(toolbarPopoverButton, 'h-10 w-10 p-0 rounded-full focus-visible:ring-0 focus-visible:ring-offset-0')
					: cn(
						'h-12 w-12 rounded-full bg-black/50 hover:bg-black/60 backdrop-blur-md transition-all',
						'border border-white/25 can-hover:hover:border-white/40 focus-visible:ring-0 focus-visible:ring-offset-0'
					),
				triggerClassName
			)}
			aria-label="Menu"
		>
			<Menu className={cn(isToolbarTrigger ? 'w-5 h-5 text-foreground/80' : 'w-6 h-6 text-white/70', triggerIconClassName)} />
		</Button>
	);

	const triggerNode = triggerProp ?? defaultTrigger;

	const menuBody = (
		<>
			{user && (
				<div className="flex items-center gap-3 mt-2 mb-3 p-3 rounded-xl bg-white/[0.04] border border-white/[0.06]">
					<EditableAvatar
						avatarUrl={profile?.avatar_url}
						username={profile?.username}
						email={user?.email}
						size="lg"
						editable={true}
						onAvatarUpdated={() => refetch()}
					/>
					<div className="flex-1 min-w-0">
						<p className="font-semibold text-sm truncate">
							{profile?.username || user.email?.split('@')[0] || 'User'}
						</p>
						<p className="text-xs text-muted-foreground/80 truncate">{user.email}</p>
					</div>
				</div>
			)}

			{user ? (
				<>
					<nav className="space-y-1.5">
						<Button
							variant="ghost"
							className={cn(navItemClass, isInventoryActive && activeClass)}
							onClick={() => handleNavigation('/inventory')}
						>
							Inventory
						</Button>
						{profile?.username && (
							<Button
								variant="ghost"
								className={cn(navItemClass, isStorefrontActive && activeClass)}
								onClick={() => handleNavigation('/preview')}
							>
								Store
							</Button>
						)}
						<Button
							variant="ghost"
							className={cn(navItemClass, isTransactionsActive && activeClass)}
							onClick={() => handleNavigation('/transactions')}
						>
							Transactions
						</Button>
						{isAdmin && (
							<Button
								variant="ghost"
								className={cn(navItemClass, isAdminActive && activeClass)}
								onClick={() => handleNavigation('/admin')}
							>
								<Shield className="mr-2 h-4 w-4" />
								Admin
							</Button>
						)}
					</nav>

					<Separator className="my-3 border-white/10" />

					<div className="space-y-1.5">
						<Button variant="ghost" className={navItemClass} onClick={() => handleNavigation('/settings')}>
							<User className="mr-2 h-4 w-4" />
							Profile
						</Button>
						<Button variant="ghost" className={navItemClass} onClick={handleCopyLink}>
							<LinkIcon className="mr-2 h-4 w-4" />
							Copy Link
						</Button>
						<Button variant="ghost" className={navItemClass} onClick={handleOpenQr}>
							<QrCode className="mr-2 h-4 w-4" />
							Show QR Code
						</Button>
						<Button
							variant="ghost"
							className={cn(
								'w-full justify-start text-base font-medium h-11',
								'border border-transparent hover:border-destructive/30',
								'text-destructive hover:text-destructive hover:bg-destructive/20',
								'transition-all duration-200'
							)}
							onClick={handleSignOut}
						>
							<LogOut className="mr-2 h-4 w-4" />
							Sign Out
						</Button>
					</div>
				</>
			) : (
				<div className="flex flex-col gap-2 pt-2">
					<button
						onClick={handleCopyLink}
						className="w-full flex items-center justify-between px-4 h-14 rounded-xl text-[18px] font-semibold tracking-tight text-foreground bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.07] active:bg-white/[0.09] transition-colors"
					>
						<span>Share</span>
						<span className="text-white/40 text-xl leading-none">›</span>
					</button>
					<button
						onClick={handleOpenQr}
						className="w-full flex items-center justify-between px-4 h-14 rounded-xl text-[18px] font-semibold tracking-tight text-foreground bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.07] active:bg-white/[0.09] transition-colors"
					>
						<span>Show QR Code</span>
						<span className="text-white/40 text-xl leading-none">›</span>
					</button>
					<button
						onClick={() => { navigate('/owner'); setTimeout(() => setOpen(false), 0); }}
						className="w-full flex items-center justify-between px-4 h-14 rounded-xl text-[18px] font-semibold tracking-tight text-foreground bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.07] active:bg-white/[0.09] transition-colors"
					>
						<span>Page Owner Login</span>
						<span className="text-white/40 text-xl leading-none">›</span>
					</button>
				</div>
			)}
		</>
	);

  const qrDialog = (
    <Sheet open={qrOpen} onOpenChange={setQrOpen}>
      <SheetContent
        side="top"
        grabbable
        showCloseButton={false}
        className={cn(
          'bg-chrome border-b border-white/[0.08]',
          'rounded-b-2xl px-6 pt-[max(env(safe-area-inset-top),20px)] pb-2',
        )}
      >
        <SheetHeader className="sr-only">
          <SheetTitle>QR Code</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col items-center">
          <SimpleQrView url={qrUrl} size={200} />
        </div>
      </SheetContent>
    </Sheet>
  );

	// Desktop: right-side sheet (modal so toasts can't render over it)
	if (!isMobile) {
		return (
			<>
				<Sheet open={open} onOpenChange={setOpen}>
				<SheetTrigger asChild>{triggerNode}</SheetTrigger>
					<SheetContent
						side="right"
						showCloseButton={false}
						className={cn(
							'w-[320px] max-w-[85vw] flex flex-col',
							'backdrop-blur-md bg-black/10 dark:bg-black/20 border border-white/[0.08] shadow-[inset_0_1px_1px_rgba(255,255,255,0.03)]'
						)}
					>
						<SheetClose className="absolute right-4 top-[14px] rounded-md opacity-70 hover:opacity-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30">
							<X className="h-6 w-6" />
							<span className="sr-only">Close</span>
						</SheetClose>
						<SheetHeader className="sr-only">
							<SheetTitle>Navigation Menu</SheetTitle>
						</SheetHeader>
						<div className="mt-6 flex flex-col overflow-y-auto">{menuBody}</div>
					</SheetContent>
				</Sheet>
				{qrDialog}
			</>
		);
	}

	// Mobile: bottom drawer (iOS-native)
	return (
		<>
			<Drawer open={open} onOpenChange={setOpen} shouldScaleBackground={false}>
			<DrawerTrigger asChild>{triggerNode}</DrawerTrigger>
				<DrawerContent className={cn('bg-black/40 backdrop-blur-2xl border-t border-white/[0.08] shadow-[inset_0_1px_1px_rgba(255,255,255,0.04)] rounded-t-2xl', 'max-h-[85dvh]')}>
					<DrawerHeader className="sr-only">
						<DrawerTitle>Navigation Menu</DrawerTitle>
					</DrawerHeader>
					<div className="px-4 pt-2 pb-[max(env(safe-area-inset-bottom),16px)] flex flex-col overflow-y-auto">
						{menuBody}
						<div className="mt-4 pt-3 border-t border-white/[0.06] flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
							<span className="text-[10px] text-white/30">© 2026 CardSimp</span>
							<button
								onClick={() => handleNavigation('/safety')}
								className="text-[10px] text-white/40 hover:text-active-gold transition-colors"
							>
								Shipping &amp; Returns
							</button>
							<button
								onClick={() => handleNavigation('/terms')}
								className="text-[10px] text-white/40 hover:text-active-gold transition-colors"
							>
								Terms
							</button>
							<button
								onClick={() => handleNavigation('/privacy')}
								className="text-[10px] text-white/40 hover:text-active-gold transition-colors"
							>
								Privacy
							</button>
						</div>
					</div>
				</DrawerContent>
			</Drawer>
			{qrDialog}
		</>
	);
};
